import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { toast } from "sonner";
import { Loader2, ArrowRight, CheckCircle2, HardHat, Copy } from "lucide-react";

const CAT_LABEL = {
  common_area_cleaning: "Common-area cleaning", trash: "Trash & garbage", landscaping: "Landscaping",
  plumbing: "Plumbing", electrical: "Electrical", hvac: "HVAC", pest_control: "Pest control",
  pool: "Pool", handyman: "Handyman", janitorial: "Janitorial", other: "Other",
};

export default function VendorApply() {
  const { token } = useParams();
  const [f, setF] = useState({ name: "", contact_name: "", phone: "", email: "", website: "", trades: [], language: "en" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [community, setCommunity] = useState(null);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await api.get(`/fs/apply-info/${token}`);
        setCommunity(data.community);
      } catch { /* invalid/expired token — fall back to the generic network apply */ }
    })();
  }, [token]);

  const submit = async () => {
    if (!f.name.trim()) return toast.error("Company name is required");
    if (!f.phone.trim() && !f.email.trim()) return toast.error("Add a mobile number or an email");
    setBusy(true);
    try {
      const { data } = await api.post("/fs/vendor-apply", { ...f, community_token: token || "" });
      setDone(data);
    } catch (e) { toast.error(errMsg(e, "Couldn't submit — try again")); }
    setBusy(false);
  };

  const T = (k, ph, testid, type = "text") => (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-ink/40">{ph}</span>
      <input data-testid={testid} type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
        className="w-full rounded-xl border border-sand-300 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
    </label>
  );

  if (done) return (
    <div className="min-h-screen bg-sand-50" data-testid="vendor-apply-done">
      <div className="mx-auto max-w-xl px-5 pt-16">
        <div className="rounded-3xl bg-white px-6 py-10 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
          <h1 className="mt-3 font-heading text-2xl font-extrabold text-ink">
            {done.resume ? "You're already in our system" : "Application started"}
          </h1>
          <p className="mt-2 text-sm text-ink/55">
            {done.resume
              ? "Pick up where you left off with the link below."
              : "Finish your setup — insurance, crew and the agreement — at the link below. If you gave an email, we sent it there too."}
          </p>
          <div className="mt-5 flex items-center gap-2">
            <input readOnly value={done.link} data-testid="apply-link"
              className="min-w-0 flex-1 rounded-xl border border-sand-300 bg-sand-50 px-3 py-2.5 text-xs text-ink/60" />
            <button data-testid="apply-copy" onClick={() => copyText(done.link, "Link copied")}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-ink px-4 py-2.5 text-sm font-bold text-white"><Copy className="h-4 w-4" /> Copy</button>
          </div>
          <a href={done.link} data-testid="apply-continue"
            className="mt-3 inline-flex items-center justify-center gap-2 rounded-xl bg-redline-500 px-5 py-3 text-sm font-bold text-white hover:bg-redline-600">
            Continue setup <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-sand-50 pb-16" data-testid="vendor-apply">
      <div className="mx-auto max-w-xl px-5 pt-12">
        <div className="mb-1 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-redline-600">
          <HardHat className="h-3.5 w-3.5" /> {community ? "Vendor application" : "Vendor network"}
        </div>
        <h1 className="font-heading text-2xl font-extrabold leading-tight text-ink">
          {community ? `Apply to work with ${community}` : "Apply to join the vendor network"}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-ink/55">
          {community
            ? `Tell us about your company and add your crew. Insurance and your W-9 can wait — you can come back to the same link later. Jobs come straight to your crew by text; no accounts, no phone tag.`
            : "Tell us about your company. After a quick review, communities can add you and jobs come straight to your crew by text — no accounts, no phone tag."}{" "}
          <a href="/vendor-network" data-testid="apply-what-it-means" className="font-semibold text-redline-600 hover:underline">
            What joining means &rarr;
          </a>
        </p>
        <div className="mt-5 space-y-3">
          {T("name", "Legal business name", "apply-name")}
          {T("contact_name", "Your name (office contact)", "apply-contact")}
          <div className="grid gap-3 sm:grid-cols-2">
            {T("phone", "Mobile number", "apply-phone")}
            {T("email", "Email", "apply-email", "email")}
          </div>
          {T("website", "Website (optional)", "apply-website")}
          <div>
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-ink/40">What do you do?</div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(CAT_LABEL).map(([k, label]) => {
                const on = f.trades.includes(k);
                return (
                  <button key={k} data-testid={`apply-trade-${k}`} onClick={() => setF({
                    ...f, trades: on ? f.trades.filter((x) => x !== k) : [...f.trades, k],
                  })} className={`rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${on ? "bg-redline-500 text-white" : "bg-white text-ink/60"}`}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          <button data-testid="apply-submit" onClick={submit} disabled={busy}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-redline-500 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-redline-600 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Apply <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
